// src/shared/changelog/autoOpenRules.ts
import type { ChangelogEntry } from "./types";
import type { ChangelogMonthGroup } from "./grouping";

/**
 * The arithmetic of the What's new auto-open (CTX-500 §2.5, §2.6), kept apart
 * from the effects that read storage and open a dialog.
 *
 * Everything here is a pure function over values the caller already holds. The
 * rules are the whole of the feature — *when* it interrupts and *what* it lets
 * go of — and they are worth exercising without a React tree, a router, a clock
 * or a fetch around them.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * How long after an auto-open the next one may happen.
 *
 * The floor is what makes the modal an announcement rather than a habit: two
 * releases in one week is a normal month here, and a dialog on each would teach
 * the reader to dismiss it without reading.
 */
export const MIN_DAYS_BETWEEN_AUTO_OPENS = 7;

/**
 * How many entries one walkthrough puts in front of the reader.
 *
 * The recorrido is not the archive — `/changelog` is, and it is one click away
 * from every card. A month of twelve entries walked in full turns an
 * announcement into an obligation, so the overflow becomes a single closing
 * card pointing at the archive instead.
 */
export const MAX_ENTRIES_PER_WALKTHROUGH = 5;

/**
 * How long an `actionRequired` entry keeps re-insisting before it gives up.
 *
 * §2.6 words this as "3 auto-opens **or** 14 days, whichever comes first". With
 * the 7-day floor above, the third auto-open cannot land before day 14 — the
 * earliest possible sequence is day 0, day 7, day 14 — so the day count always
 * arrives first or ties, and a per-entry auto-open counter would be state that
 * can never decide anything. That is why there is no third storage key here and
 * no count is kept: the two clauses collapse into this one.
 */
export const ACTION_REQUIRED_INSISTENCE_DAYS = 14;

/**
 * The last auto-open: when, and for which release.
 *
 * Both facts live in one record because §4 of the decision names exactly one
 * key for this (`ctx:changelog:lastAutoOpenAt`) and §2.5 asks two questions of
 * it — "has it been 7 days" and "did this release already have its turn". A
 * timestamp alone answers only the first, and after seven days it would let a
 * release the reader already dismissed interrupt them a second time.
 */
export interface AutoOpenRecord {
  /** ISO timestamp of the auto-open. */
  at: string;
  /** `ChangelogMonthGroup.id` (`YYYY-MM`) of the release that was shown. */
  releaseId: string;
}

function daysSince(iso: string, now: number): number {
  const elapsed = now - Date.parse(iso);
  // NaN fails every comparison, so a malformed timestamp would read as "no time
  // has passed" and suppress the feature forever. Treated as ancient instead:
  // the floor is there to avoid nagging, not to be a lock nobody can clear.
  if (Number.isNaN(elapsed)) return Number.POSITIVE_INFINITY;
  return elapsed / MS_PER_DAY;
}

/**
 * Whether the modal should open itself for this release (§2.1, §2.5).
 *
 * The caller has already decided that the *moment* is right — a product view,
 * no tour running, the feed and the seen set both loaded. This decides whether
 * there is anything worth interrupting for.
 */
export function shouldAutoOpenRelease({
  release,
  seenEntries,
  lastAutoOpen,
  now,
}: {
  release: ChangelogMonthGroup | null;
  seenEntries: Set<string>;
  lastAutoOpen: AutoOpenRecord | null;
  now: number;
}): boolean {
  if (!release || release.entries.length === 0) return false;

  // Nothing unread is nothing to announce. This is also what makes a seeded
  // first session quiet (§2.8) without the seeding needing its own clause.
  const hasUnseen = release.entries.some(
    (entry) => !seenEntries.has(entry.entryKey)
  );
  if (!hasUnseen) return false;

  if (!lastAutoOpen) return true;

  // One per release, even once the floor has passed. A reader who closed this
  // release's walkthrough at `1 of 3` said something; re-opening it a week
  // later would be asking again rather than announcing.
  if (lastAutoOpen.releaseId === release.id) return false;

  return daysSince(lastAutoOpen.at, now) >= MIN_DAYS_BETWEEN_AUTO_OPENS;
}

/**
 * The slice of a release the walkthrough actually walks, and what is left over.
 *
 * `overflowCount` drives the closing `N más en el changelog →` card; zero means
 * the walkthrough covers the release and there is no closing card.
 */
export function splitWalkthrough(entries: ChangelogEntry[]): {
  walked: ChangelogEntry[];
  overflowCount: number;
} {
  const walked = entries.slice(0, MAX_ENTRIES_PER_WALKTHROUGH);
  return { walked, overflowCount: entries.length - walked.length };
}

/**
 * True while an `actionRequired` entry is still allowed to re-insist (§2.6).
 *
 * Measured from the entry's own `releasedAt`, like the rail's `New` window: a
 * release is a month, so a group-wide age would give an entry published on the
 * 28th two days of insistence and one from the 1st a fortnight.
 */
function isStillInsisting(entry: ChangelogEntry, now: number): boolean {
  if (!entry.actionRequired) return false;
  return daysSince(entry.releasedAt, now) < ACTION_REQUIRED_INSISTENCE_DAYS;
}

/**
 * How the reader left the walkthrough, which is what decides how much of it
 * counts as read (§2.2, §2.6).
 *
 * `complete` is `Done` on the last card — the whole recorrido. `dismiss` is
 * everything else: `✕`, Esc, the backdrop, `View changelog`, or leaving to run
 * a walkthrough's tour.
 */
export type WalkthroughExit = "complete" | "dismiss";

/**
 * Which of the entries the reader met may be marked read.
 *
 * Two kinds of entry survive a dismissal unread, and they are the two §2.6
 * singles out:
 *
 * * **An entry with a `walkthrough`** is not read by being scrolled past — it
 *   is read by taking the tour, so only the tour finishing or being skipped
 *   marks it (`markWalkthroughEntrySeen`). A `✕` leaves it standing. `Done`
 *   does mark it, because reaching the end of the recorrido is the reader
 *   saying they are through it.
 * * **An `actionRequired` entry** keeps re-insisting for
 *   `ACTION_REQUIRED_INSISTENCE_DAYS` whatever the reader does, because it is
 *   the one kind of entry that asks them to go and do something. Nothing in
 *   the dialog is an acknowledgement, so neither exit clears it early.
 *
 * Everything else is informative and never re-insists: meeting it once is
 * reading it.
 */
export function entriesToMarkOnExit(
  shown: ChangelogEntry[],
  exit: WalkthroughExit,
  now: number
): string[] {
  return shown
    .filter((entry) => {
      if (isStillInsisting(entry, now)) return false;
      if (exit === "dismiss" && entry.walkthrough) return false;
      return true;
    })
    .map((entry) => entry.entryKey);
}
